import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  FormMessage,
  Label,
  Select,
  theme,
  Flex,
  Text,
} from '@xometry/xometry_loft';

interface Props {
  label?: string;
  isModal?: boolean;
  suggestions: ListOption[];
  // setState function for the list of selectedEmails
  setSelectedEmails: (emails: string[]) => void;
  // setState function for whether or not selectedEmails are all valid
  setAreEmailsValid: (isValid: boolean) => void;
}

interface SelectedValue {
  value: string;
  label: string;
  invalid?: boolean;
}

interface ListOption {
  first_name: string;
  last_name: string;
  email: string;
}
const isEmailValid = (email: string) =>
  /^[a-zA-Z0-9.%&’+/=?^_`'{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]{2,})+$/.test(
    email
  );

const EmailSelectCreatable = ({
  label,
  suggestions,
  setSelectedEmails,
  setAreEmailsValid,
  isModal = false,
}: Props) => {
  const [selectedValues, setSelectedValues] = useState<SelectedValue[]>([]);
  const selectRef = useRef(null);
  const [currentInput, setCurrentInput] = useState('');
  const [selectIsOpen, setSelectIsOpen] = useState(false);

  useEffect(() => {
    // Update setter props so parent component knows the list of emails and if they are valid
    setSelectedEmails([...selectedValues.map(({ value }) => value)]);
    const areAllEmailsValid = selectedValues.every(
      (selectedValue) => !selectedValue.invalid
    );
    setAreEmailsValid(areAllEmailsValid);
  }, [selectedValues]);

  const formatLabelString = (email: string) => {
    const userMatch = suggestions.filter(
      (suggestion) => suggestion.email === email
    );
    if (userMatch[0]?.first_name && userMatch[0]?.last_name) {
      return `${userMatch[0].first_name} ${userMatch[0].last_name || ''}`;
    }
    return email;
  };

  const handleSelectChange = (selectedOptions: SelectedValue[] | null) => {
    // When this fn is fired for changes such as clearing all values or removing the last value, selectedOptions is null.
    const validatedSelectedOptions = selectedOptions
      ? selectedOptions.map((option) => ({
          value: option.value,
          label: formatLabelString(option.value),
          invalid: !isEmailValid(option.value),
        }))
      : [];
    setSelectedValues([...validatedSelectedOptions]);
  };

  const formatCreateLabel = (inputString: string) => {
    /**
     * Users might paste in multiple emails at once, so make it appear as if they are
     * adding each individual email as its own option
     */
    const emailsArr = inputString
      .split(/,|\s+/)
      .filter((email) => email.trim() !== '');
    const uniqueEmailsArr = [...new Set(emailsArr)];

    if (uniqueEmailsArr.length > 1) {
      return `Add "${uniqueEmailsArr.join(`", "`)}"`;
    }
    return `Add ${uniqueEmailsArr.map((email) => `"${email}"`)}`;
  };

  const handleCreateOption = (inputString: string) => {
    /**
     * When users paste in multiple emails at once, we need
     * to split them off into their own values and ignore duplicates
     */
    const emailsArr = inputString
      .split(/,|\s+/)
      .filter((email) => email.trim() !== '');
    const uniqueEmailsArr = [...new Set(emailsArr)];

    const currentValuesStrings = selectedValues.map(({ value }) => value);
    const newValidatedValues = uniqueEmailsArr
      .map((email: string) => ({
        value: email,
        label: formatLabelString(email),
        invalid: !isEmailValid(email),
      }))
      .filter(({ value }) => !currentValuesStrings.includes(value));

    setSelectedValues([...selectedValues, ...newValidatedValues]);
  };

  const handleInputChange = (value: string) => {
    setCurrentInput(value);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLSelectElement>) => {
    const currentInputValue = e.target.value;
    if (e.key === 'Backspace' && !e.repeat && currentInputValue === '') {
      e.preventDefault();
      const newList = [...selectedValues].slice(0, -1);
      setSelectedValues(newList);
    } else if (e.key === ' ' || e.key === ',' || e.key === 'Enter') {
      e.preventDefault();
      if (currentInput.length > 0) {
        handleCreateOption(currentInputValue);
        setCurrentInput('');
      }
    }
  };

  const onPaste = (e: ClipboardEvent) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('Text');
    handleCreateOption(pasteData);
  };

  const customOptionRow = (suggestion) => {
    const displayName = suggestion.first_name
      ? `${suggestion.first_name} ${suggestion.last_name}`
      : '';
    return (
      <Flex
        flexDirection="row"
        paddingX={theme.space[3]}
        color={theme.colors.darkBlues[90]}
        alignItems="top"
        gap={theme.space[1]}
      >
        <Flex width="30px" height="30px" alignItems="center">
          <i className="far fa-circle-user fa-lg" />
        </Flex>
        <Flex flexDirection="column" paddingX={'4px'} height={'52px'}>
          {suggestion.first_name && (
            <Text fontSize={theme.fontSizes[2]}>{displayName}</Text>
          )}
          <Text fontSize={theme.fontSizes[suggestion.first_name ? 1 : 2]}>
            {suggestion.email}
          </Text>
        </Flex>
      </Flex>
    );
  };

  // manually adjust the height of of the component to compensate for DOM limitations
  const calcHeight = () => {
    const suggestionEmails = suggestions.map((obj) => obj.email);
    const selectedEmailValues = selectedValues.map((obj) => obj.value);
    const menuHeight =
      (suggestions.length -
        suggestionEmails.filter((email) => selectedEmailValues.includes(email))
          .length) *
      55;
    return (selectIsOpen && isModal && menuHeight) > 55
      ? `${Math.min(menuHeight, 300)}px`
      : 'fit-content';
  };

  return (
    <Box marginBottom={theme.space[4]} onPaste={onPaste} height={calcHeight()}>
      <Flex gap={theme.space[1]} ref={selectRef}>
        <Label htmlFor="itemsList">{label}</Label>
        <Text
          kind="body-sm"
          fontWeight={theme.fontWeights.regular}
          color={theme.colors.darkBlues[75]}
        >
          <i>Optional</i>
        </Text>
      </Flex>
      <Select
        creatable
        options={suggestions.map((suggestion) => ({
          value: suggestion.email,
          label: customOptionRow(suggestion),
        }))}
        isMulti
        fullWidth
        suppressCheckbox
        value={selectedValues}
        onKeyDown={handleKeyDown}
        inputValue={currentInput}
        onInputChange={handleInputChange}
        onBlur={(e) => {
          handleCreateOption(e.target.value);
        }}
        onMenuClose={() => setSelectIsOpen(false)}
        onMenuOpen={() => setSelectIsOpen(true)}
        onChange={handleSelectChange}
        onCreateOption={handleCreateOption}
        createOptionPosition="first"
        placeholder="Select or type an email address..."
        noOptionsMessage={() => 'Type an email address...'}
        hideSelectedOptions
        blurInputOnSelect={false}
        closeMenuOnSelect={false}
        formatCreateLabel={formatCreateLabel}
      />
      {selectedValues.some((option) => option?.invalid) && (
        <FormMessage invalid>
          Remove or correct any invalid email addresses to continue.
        </FormMessage>
      )}
    </Box>
  );
};

export default EmailSelectCreatable;
